/**
 * CustomerCreditNotesPanel - Display credit notes for a customer
 *
 * Shows all credit notes across all invoices for a customer with:
 * - Date, credit note number, type, invoice reference
 * - Amount and reason
 * - Ability to create new credit notes
 *
 * US-REC-001: Invoice Reconciliation - Customer View Integration
 */

import { useState, useEffect, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { FileText, Plus, CreditCard, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { CreditNoteTypeBadge } from '@/components/credit-notes/CreditNoteTypeBadge'
import { CreditNoteModal } from '@/components/credit-notes/CreditNoteModal'
import { useCreditNotes } from '@/hooks/useCreditNotes'
import { fetchCustomerBalanceDetail, supabase } from '../supabase-client'

/**
 * Format currency value for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount)
}

/**
 * Format date for display
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  try {
    return format(parseISO(dateStr), 'dd MMM yyyy')
  } catch {
    return dateStr
  }
}

/**
 * CustomerCreditNotesPanel displays all credit notes for a customer
 * @param {Object} props - Component props
 * @param {string} props.customerId - Customer ID to show credit notes for
 */
export default function CustomerCreditNotesPanel({ customerId }) {
  const [isLoading, setIsLoading] = useState(true)
  const [balanceDetail, setBalanceDetail] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false)

  // Fetch credit notes for this customer
  const {
    creditNotes,
    totalCredited,
    isLoading: creditNotesLoading,
    error: creditNotesError,
    refetch: refetchCreditNotes
  } = useCreditNotes({
    customerId,
    autoFetch: true
  })

  // Load balance detail and unpaid invoices
  useEffect(() => {
    const loadData = async () => {
      if (!customerId) return

      setIsLoading(true)
      try {
        // Fetch balance detail
        const balance = await fetchCustomerBalanceDetail(customerId)
        setBalanceDetail(balance)

        // Fetch unpaid invoices for credit note creation
        const { data: invoiceData, error: invoiceError } = await supabase
          .from('invoice_balances')
          .select('invoice_id, invoice_no, invoice_total, amount_due, calculated_status, is_legacy_paid')
          .eq('customer_id', customerId)
          .gt('amount_due', 0)
          .order('date', { ascending: false })

        if (invoiceError) throw invoiceError

        setInvoices(invoiceData || [])
      } catch (error) {
        console.error('[CustomerCreditNotesPanel] Error loading data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [customerId])

  // Handle successful credit note creation
  const handleCreditNoteSuccess = () => {
    refetchCreditNotes()
    // Refresh balance detail
    fetchCustomerBalanceDetail(customerId).then(setBalanceDetail)
    // Refresh invoices list
    supabase
      .from('invoice_balances')
      .select('invoice_id, invoice_no, invoice_total, amount_due, calculated_status, is_legacy_paid')
      .eq('customer_id', customerId)
      .gt('amount_due', 0)
      .order('date', { ascending: false })
      .then(({ data }) => setInvoices(data || []))
  }

  // Handle opening credit note modal for a specific invoice
  const handleCreateCreditNote = (invoice = null) => {
    if (invoice) {
      // Transform invoice data to match what CreditNoteModal expects
      setSelectedInvoice({
        id: invoice.invoice_id,
        invoice_no: invoice.invoice_no,
        customer_id: customerId,
        amount_due: invoice.amount_due,
        is_legacy_paid: invoice.is_legacy_paid
      })
    } else if (invoices.length > 0) {
      // Default to first unpaid invoice
      const firstInvoice = invoices[0]
      setSelectedInvoice({
        id: firstInvoice.invoice_id,
        invoice_no: firstInvoice.invoice_no,
        customer_id: customerId,
        amount_due: firstInvoice.amount_due,
        is_legacy_paid: firstInvoice.is_legacy_paid
      })
    } else {
      // No invoices available
      setSelectedInvoice(null)
    }
    setShowCreditNoteModal(true)
  }

  // Sort credit notes by date descending
  const sortedCreditNotes = useMemo(() => {
    return [...(creditNotes || [])].sort((a, b) => {
      return new Date(b.date) - new Date(a.date)
    })
  }, [creditNotes])

  const loading = isLoading || creditNotesLoading

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Credit Notes</CardTitle>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCreateCreditNote()}
            disabled={loading || invoices.length === 0}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Credit Note
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Balance Summary */}
        {balanceDetail && (
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Payments</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(balanceDetail.total_credits)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Outstanding</p>
              <p className="text-lg font-semibold text-amber-600">
                {formatCurrency(balanceDetail.total_outstanding)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Credited</p>
              <p className="text-lg font-semibold text-blue-600">
                {formatCurrency(totalCredited)}
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin" />
            <span className="ml-2 text-muted-foreground">Loading credit notes...</span>
          </div>
        )}

        {/* Error State */}
        {creditNotesError && (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
            <AlertCircle className="h-5 w-5" />
            <span>Error loading credit notes: {creditNotesError}</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && !creditNotesError && sortedCreditNotes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-muted-foreground">No credit notes for this customer</p>
            {invoices.length > 0 && (
              <Button
                variant="link"
                className="mt-2"
                onClick={() => handleCreateCreditNote()}
              >
                Create the first credit note
              </Button>
            )}
          </div>
        )}

        {/* Credit Notes Table */}
        {!loading && !creditNotesError && sortedCreditNotes.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Credit Note #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCreditNotes.map((cn) => (
                <TableRow key={cn.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(cn.date)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {cn.credit_note_no}
                  </TableCell>
                  <TableCell>
                    <CreditNoteTypeBadge type={cn.type} />
                  </TableCell>
                  <TableCell>
                    {cn.invoice?.invoice_no || cn.invoice_no || '-'}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-blue-600">
                    {formatCurrency(cn.amount)}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate" title={cn.reason}>
                    {cn.reason}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Unpaid Invoices Section */}
        {invoices.length > 0 && !loading && (
          <div className="mt-6 pt-4 border-t">
            <p className="text-sm font-medium text-muted-foreground mb-3">
              Invoices with Outstanding Balance ({invoices.length})
            </p>
            <div className="space-y-2">
              {invoices.slice(0, 5).map((inv) => (
                <div
                  key={inv.invoice_id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleCreateCreditNote(inv)}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{inv.invoice_no}</span>
                    {inv.is_legacy_paid && (
                      <span className="text-xs text-amber-600">(Legacy)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {formatCurrency(inv.amount_due)} due
                    </span>
                    <Button variant="ghost" size="sm">
                      <Plus className="h-3 w-3 mr-1" />
                      Credit
                    </Button>
                  </div>
                </div>
              ))}
              {invoices.length > 5 && (
                <p className="text-sm text-muted-foreground text-center pt-2">
                  +{invoices.length - 5} more invoices
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {/* Credit Note Modal */}
      {selectedInvoice && (
        <CreditNoteModal
          invoice={selectedInvoice}
          open={showCreditNoteModal}
          onOpenChange={setShowCreditNoteModal}
          onSuccess={handleCreditNoteSuccess}
        />
      )}
    </Card>
  )
}
